import React from "react";

export interface CircleProps {
  progress: number; // 0~100
  size?: "sm" | "md";
}

// SVG 크기 (내부 원형)
const SVG_SIZE_MAP = {
  sm: 32, // Figma 모바일 디자인에 맞춤 (32x32px)
  md: 48, // Figma 디자인에 맞춤 (48x48px)
};
// 전체 컨테이너 크기 (패딩 포함)
const CONTAINER_SIZE_MAP = {
  sm: 56, // 모바일: 32 + 12*2 (Figma 디자인에 맞춤)
  md: 64, // 48 + 8*2 (Figma 디자인에 맞춤)
};
// 패딩
const PADDING_MAP = {
  sm: 12, // Figma 모바일 디자인에 맞춤 (상하좌우 12px)
  md: 8, // Figma 디자인에 맞춤 (상하좌우 8px)
};
const STROKE_WIDTH_MAP = {
  sm: 3, // Figma 모바일 디자인에 맞춤
  md: 6, // Figma 디자인에 맞춤
};
// radius = (size - strokeWidth) / 2
const RADIUS_MAP = {
  sm: 14.5, // (32 - 3) / 2
  md: 21, // (48 - 6) / 2
};

export default function Circle({ progress, size = "md" }: CircleProps) {
  const percent = Math.max(0, Math.min(progress, 100));
  const svgSize = SVG_SIZE_MAP[size];
  const containerSize = CONTAINER_SIZE_MAP[size];
  const padding = PADDING_MAP[size];
  const radius = RADIUS_MAP[size];
  const strokeWidth = STROKE_WIDTH_MAP[size];
  const circumference = 2 * Math.PI * radius;
  const dash = (percent / 100) * circumference;
  const viewBox = `0 0 ${svgSize} ${svgSize}`;
  return (
    <>
      <style>{`
        @keyframes fadeScale {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes scaleIn {
          0% {
            opacity: 0;
            transform: rotate(180deg) scaleY(-1) scale(0.5);
          }
          50% {
            transform: rotate(180deg) scaleY(-1) scale(1.2);
          }
          100% {
            opacity: 1;
            transform: rotate(180deg) scaleY(-1) scale(1);
          }
        }
      `}</style>
      <div
        className="relative flex items-center justify-center"
        style={{ width: containerSize, height: containerSize, padding }}
        data-name="progress/circle"
      >
        <svg width={svgSize} height={svgSize} viewBox={viewBox}>
          {/* Track */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            stroke="#eeeeee"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Indicator */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            stroke="#6d2ef2"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - dash}
            strokeLinecap="butt"
            transform={`rotate(-90 ${svgSize / 2} ${svgSize / 2})`}
            style={{
              transition: "stroke-dashoffset 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        </svg>
        {/* Percent Text or Icon */}
        <div
          className="absolute left-1/2 top-1/2 flex items-center justify-center"
          style={{ transform: "translate(-50%, -50%)" }}
        >
          {percent === 100 ? (
            <span
              key="emoji"
              className={size === "md" ? "text-[24px]" : "text-[16px]"}
              style={{
                transform: "rotate(180deg) scaleY(-1)",
                animation: "scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            >
              🥳
            </span>
          ) : (
            <span
              key={`percent-${percent}`}
              className={`flex items-center font-bold text-[#888888] tracking-[-0.24px] ${
                size === "sm"
                  ? "text-[12px] leading-[16px]"
                  : "text-[16px] leading-[24px]"
              }`}
              style={{
                animation: "fadeScale 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {percent}
              <span className={size === "sm" ? "text-[8px]" : "text-[12px]"}>
                %
              </span>
            </span>
          )}
        </div>
      </div>
    </>
  );
}
